import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, UserPlus, Users, Copy, RefreshCw } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

function generatePassword(len = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  const arr = new Uint32Array(len);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
}

export default function AdminEditors() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-editors"],
    queryFn: async () => (await api.get("/admin/editors")).data,
  });
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("Email and password required");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/admin/editors", form);
      toast.success(`Editor ${form.email} invited`);
      setForm({ name: "", email: "", password: "" });
      qc.invalidateQueries({ queryKey: ["admin-editors"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add editor");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, email) => {
    if (!confirm(`Remove editor ${email}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/editors/${id}`);
      toast.success("Editor removed");
      qc.invalidateQueries({ queryKey: ["admin-editors"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  const editors = data?.data || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="rounded-xl bg-white border border-slate-200 p-4 sm:p-6 space-y-4" data-testid="editor-invite-form">
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-[#D91E2A]" />
          <h3 className="font-heading font-bold text-lg text-slate-900">Invite Editor</h3>
        </div>
        <p className="text-xs text-slate-500 font-mono-fin">Share the credentials privately with the editor. They can log in at /admin/login.</p>
        <div>
          <Label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Full name (optional)</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Aditya Menon" className="mt-1.5 bg-slate-50 border-slate-200" data-testid="editor-name-input" />
        </div>
        <div>
          <Label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Email</Label>
          <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" placeholder="editor@example.com" className="mt-1.5 bg-slate-50 border-slate-200 font-mono-fin" data-testid="editor-email-input" />
        </div>
        <div>
          <Label className="text-xs font-mono-fin uppercase tracking-widest text-slate-500">Temporary password (min 8 chars)</Label>
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <PasswordInput value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Type or generate" testid="editor-password-input" />
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, password: generatePassword() })} className="mt-1.5 shrink-0 border-slate-200" data-testid="editor-password-generate" title="Generate secure password">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>
          {form.password && (
            <button
              type="button"
              onClick={() => {
                const txt = `Nivesh Street editor login\nURL: ${window.location.origin}/admin/login\nEmail: ${form.email}\nPassword: ${form.password}`;
                navigator.clipboard.writeText(txt);
                toast.success("Credentials copied — paste into WhatsApp / email");
              }}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-mono-fin uppercase tracking-wider text-slate-600 hover:text-slate-900"
              data-testid="editor-copy-creds"
            >
              <Copy className="w-3 h-3" /> Copy login details
            </button>
          )}
        </div>
        <Button type="submit" disabled={busy} className="bg-[#D91E2A] hover:bg-[#B01820] text-white font-semibold" data-testid="editor-invite-submit">
          {busy ? "Adding…" : "Add Editor"}
        </Button>
      </form>

      <div className="rounded-xl bg-white border border-slate-200 divide-y divide-slate-100" data-testid="editor-list">
        <div className="p-4 flex items-center gap-2 border-b border-slate-100">
          <Users className="w-4 h-4 text-slate-600" />
          <h3 className="font-heading font-bold text-lg text-slate-900">Editors ({editors.length})</h3>
        </div>
        {isLoading && <div className="p-6 text-sm text-slate-500 font-mono-fin">Loading…</div>}
        {!isLoading && editors.length === 0 && (
          <div className="p-6 text-sm text-slate-500 font-mono-fin">No editors yet. Add one to grant publishing access.</div>
        )}
        {editors.map(ed => (
          <div key={ed.id} className="p-4 flex items-center justify-between gap-3" data-testid={`editor-row-${ed.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-900 truncate">{ed.name}</div>
              <div className="text-[10px] font-mono-fin uppercase text-slate-500 mt-0.5 truncate">
                {ed.email} · {ed.last_login ? `Last login ${new Date(ed.last_login).toLocaleDateString()}` : "Never logged in"}
              </div>
            </div>
            <button onClick={() => remove(ed.id, ed.email)} className="p-2 rounded-md hover:bg-red-50 text-red-600 shrink-0" data-testid={`editor-remove-${ed.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
